import React, { useState, useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Heading from "@tiptap/extension-heading";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import axios from "../util/axiosConfig";

import "../assets/css/component/TiptapEditor.css";
import LineHeightCustom from "./tiptapextension/LineHeight";

type Props = {
	content?: string;
	onChange?: (html: string) => void;
	onAttachmentSelected?: (file: File) => void;
};

const TiptapEditor: React.FC<Props> = ({ content = "", onChange, onAttachmentSelected }) => {
	const [lineHeight, setLineHeight] = useState<number>(1.6);

	const editor = useEditor({
		extensions: [
			StarterKit,
			Heading.configure({ levels: [1, 2, 3] }),
			Placeholder.configure({ placeholder: "내용을 입력하세요..." }),
			Bold,
			Italic,
			Underline,
			TextAlign.configure({ types: ["heading", "paragraph"],
				defaultAlignment: "left"}),
			LineHeightCustom.configure({
				types: ["paragraph", "heading"],
				defaultLineHeight: "1.6",
			}),
			TextStyle,
			Color.configure({ types: ["textStyle"] }),
			Highlight.configure({ multicolor: true }),
			// Keep images inside a paragraph so the existing text-alignment
			// controls can align the selected image left, center, or right.
			Image.configure({ inline: true }),
		],
		content,
		onUpdate: ({ editor }) => {
			onChange?.(editor.getHTML());
		},
	});

	// ✅ 외부 content prop이 변경되면 에디터에 반영
	useEffect(() => {
		if (editor && content !== editor.getHTML()) {
			editor.commands.setContent(content);
		}
	}, [content, editor]);

	// ✅ 현재 문단 행간 반영
	useEffect(() => {
		if (!editor) return;
		const updateLineHeight = () => {
			const attrs = editor.getAttributes("paragraph");
			if (attrs.lineHeight) setLineHeight(parseFloat(attrs.lineHeight));
		};
		editor.on("selectionUpdate", updateLineHeight);
		editor.on("transaction", updateLineHeight);
		return () => {
			editor.off("selectionUpdate", updateLineHeight);
			editor.off("transaction", updateLineHeight);
		};
	}, [editor]);

	if (!editor) return null;

	// 행간 조절
	const increaseLineHeight = () => {
		const newValue = Math.min(lineHeight + 0.2, 5);
		setLineHeight(newValue);
		editor.chain().focus().setLineHeight(newValue.toString()).run();
	};
	const decreaseLineHeight = () => {
		const newValue = Math.max(lineHeight - 0.2, 0.4);
		setLineHeight(newValue);
		editor.chain().focus().setLineHeight(newValue.toString()).run();
	};

	// 색상/배경색
	const handleColorChange = (color: string) => {
		editor.chain().focus().setColor(color).run();
	};
	const handleHighlight = (color: string) => {
		editor.chain().focus().setMark("highlight", { color }).run();
	};

	// 이미지 업로드
	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;
		if (!file.type.startsWith("image/")) {
			onAttachmentSelected?.(file);
			return;
		}
		if (file.size > 10 * 1024 * 1024) {
			alert("이미지는 10MB까지 업로드할 수 있습니다.");
			return;
		}
		const insertionPosition = editor.state.selection.from;
		const formData = new FormData();
		formData.append("file", file);

		try {
			const res = await axios.post<{url: string}>("/api/upload/image", formData);
			if (res.data.url) {
				editor.chain().focus().insertContentAt(insertionPosition, {
					type: "image", attrs: {src: res.data.url, alt: file.name}
				}).run();
			} else {
				alert("이미지 업로드 실패");
			}
		} catch (err) {
			console.error("이미지 업로드 실패:", err);
		}
	};

	return (
		<div className="tiptap-wrapper">
			{/* 툴바 */}
			<div className="tiptap-toolbar">
				{/* 글자 스타일 */}
				<button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive("bold") ? "active" : ""}>B</button>
				<button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive("italic") ? "active" : ""}>I</button>
				<button onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive("underline") ? "active" : ""}>U</button>

				{/* 제목/본문 */}
				<button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive("heading", { level: 1 }) ? "active" : ""}>H1</button>
				<button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive("heading", { level: 2 }) ? "active" : ""}>H2</button>
				<button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive("heading", { level: 3 }) ? "active" : ""}>H3</button>
				<button onClick={() => editor.chain().focus().setParagraph().run()} className={editor.isActive("paragraph") ? "active" : ""}>본문</button>

				{/* 리스트 */}
				<button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive("bulletList") ? "active" : ""}>• List</button>

				{/* 정렬 */}
				<button title="글자·이미지 왼쪽 정렬" onClick={() => editor.chain().focus().setTextAlign("left").run()} className={editor.isActive({ textAlign: "left" }) ? "active" : ""}>⬅</button>
				<button title="글자·이미지 가운데 정렬" onClick={() => editor.chain().focus().setTextAlign("center").run()} className={editor.isActive({ textAlign: "center" }) ? "active" : ""}>⬍</button>
				<button title="글자·이미지 오른쪽 정렬" onClick={() => editor.chain().focus().setTextAlign("right").run()} className={editor.isActive({ textAlign: "right" }) ? "active" : ""}>➡</button>
				<button onClick={() => editor.chain().focus().setTextAlign("justify").run()} className={editor.isActive({ textAlign: "justify" }) ? "active" : ""}>⬌</button>

				{/* 행간 */}
				<button onClick={decreaseLineHeight}>－</button>
				<span className="line-height-display">{lineHeight.toFixed(1)}</span>
				<button onClick={increaseLineHeight}>＋</button>

				{/* 색상 */}
				<input type="color" onChange={(e) => handleColorChange(e.target.value)} title="글자 색상" />
				<input type="color" onChange={(e) => handleHighlight(e.target.value)} title="배경색" />

				{/* 이미지 업로드 */}
				{/*<label className="image-upload-btn">
					📷
					<input type="file" accept="image/*" hidden onChange={handleImageUpload} />
				</label>*/}
			{onAttachmentSelected && <label className="image-upload-btn">
				이미지·파일 추가
				<input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.txt" hidden onChange={handleImageUpload} />
			</label>}
			</div>

			{/* 에디터 */}
			<EditorContent
				editor={editor}
				className="tiptap-editor"
				onClick={(e) => {
					const target = e.target as HTMLElement;
					if (target.classList.contains("tiptap-editor")) {
						editor.commands.focus("end");
					}
				}}
			/>
		</div>
	);
};

export default TiptapEditor;
